import { useEditEndpointOIDCProvider } from '#api/endpoint-oidc-providers.queries.ts';
import { trackError } from '#api/errors.ts';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';

type Props = {
  onError: (errMsg: string | null) => void;
  onSuccess: () => void;
};

/** Used to change which endpoint OIDC provider is enabled when multiple are using the same verified domain. */
export function useMoveActiveProviderMutation({ onError, onSuccess }: Props) {
  const editMutation = useEditEndpointOIDCProvider();

  return async function moveProvider(fromProvider: EndpointOIDC, toProvider: EndpointOIDC) {
    onError(null);

    // If any errors occur, don't proceed.
    try {
      // Start by disabling the original provider
      await editMutation.mutateAsync({
        id: fromProvider.id,
        values: { ...fromProvider, clientSecret: null, enabled: false },
      });

      // We've disabled the original provider, now we have to enable the new one
      try {
        await editMutation.mutateAsync({
          id: toProvider.id,
          values: { ...toProvider, clientSecret: null, enabled: true },
        });

        // If we made it this far, it worked
        onSuccess();
      } catch (error) {
        trackError(error);
        onError(`We couldn’t enable ${toProvider.name}, please try again or contact support`);
      }
    } catch (error) {
      trackError(error);
      onError(`We couldn’t disable ${fromProvider.name}, please try again or contact support`);
    }
  };
}
